import { Controller } from "@hotwired/stimulus"
import consumer from "../channels"

// Live updates for the conversations list: subscribes to InboxChannel and, on a
// new/updated conversation, replaces + moves its list item to the top. Inbound
// messages also play a notification chime and flash the tab title.
export default class extends Controller {
  static targets = ["list"]
  static values = {
    organizationId: Number,
    currentConversationId: Number,
    listUrl: String,
    // "No leídos" filter: a broadcast about an already-read conversation must
    // not push it into a list that is meant to hold only unread ones.
    unreadOnly: Boolean,
    // The list is a long-lived DOM nothing re-renders: resync it from the
    // server every hour, or sooner once broadcasts have grown it past the
    // cap — but only at an idle moment, never under an operator's hands.
    resyncInterval: { type: Number, default: 60 * 60 * 1000 },
    maxItems: { type: Number, default: 500 },
    idleAfter: { type: Number, default: 60 * 1000 }
  }

  connect() {
    this.originalTitle = document.title
    this.audioCtx = null
    this.lastActivity = Date.now()
    this.lastResync = Date.now()

    this.activityHandler = () => { this.lastActivity = Date.now() }
    ;["keydown", "pointerdown", "wheel", "touchstart"].forEach((name) =>
      document.addEventListener(name, this.activityHandler, { passive: true })
    )
    this.resyncTimer = setInterval(() => this.maybeResync(), 30 * 1000)
    this.frameLoadHandler = (event) => this.listResynced(event)
    this.element.addEventListener("turbo:frame-load", this.frameLoadHandler)
    // The panel can navigate away from a conversation (e.g. "marcar como no
    // leído" lands on the empty state): drop the selection when it does.
    this.panelLoadHandler = (event) => this.panelLoaded(event)
    document.addEventListener("turbo:frame-load", this.panelLoadHandler)

    // Browsers block audio until a user gesture — unlock on first interaction.
    this.armAudio = () => this.ensureAudio()
    document.addEventListener("click", this.armAudio, { once: true })
    document.addEventListener("keydown", this.armAudio, { once: true })

    this.visibilityHandler = () => { if (!document.hidden) this.clearTitleFlash() }
    document.addEventListener("visibilitychange", this.visibilityHandler)

    // Opening a conversation swaps only the right panel (Turbo Frame), so the
    // list is never re-rendered: mirror the server-side selection here. Bound
    // at document level because search results live outside this element.
    this.selectHandler = (event) => this.select(event)
    document.addEventListener("click", this.selectHandler)

    if (!this.organizationIdValue) return
    this.subscription = consumer.subscriptions.create(
      { channel: "InboxChannel", organization_id: this.organizationIdValue },
      { received: (data) => this.received(data) }
    )
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
    document.removeEventListener("visibilitychange", this.visibilityHandler)
    document.removeEventListener("click", this.selectHandler)
    ;["keydown", "pointerdown", "wheel", "touchstart"].forEach((name) =>
      document.removeEventListener(name, this.activityHandler)
    )
    clearInterval(this.resyncTimer)
    this.element.removeEventListener("turbo:frame-load", this.frameLoadHandler)
    document.removeEventListener("turbo:frame-load", this.panelLoadHandler)
    this.clearTitleFlash()
  }

  panelLoaded(event) {
    if (event.target.id !== "conversation_panel") return
    const src = event.target.getAttribute("src") || ""
    if (/\/admin\/conversations\/\d+/.test(src)) return

    document.querySelectorAll("a.conversation-item").forEach((el) => {
      el.classList.remove(...this.constructor.SELECTED_CLASSES)
    })
    this.currentConversationIdValue = 0
  }

  // --- Periodic resync ------------------------------------------------------

  get itemCount() {
    return this.listTarget.querySelectorAll("a.conversation-item").length
  }

  get idle() {
    if (Date.now() - this.lastActivity < this.idleAfterValue) return false
    // A half-written message means someone is working, however still the mouse.
    const drafts = [...document.querySelectorAll("#conversation_panel textarea")]
    return !drafts.some((t) => t.value.trim().length > 0)
  }

  maybeResync() {
    if (!this.hasListTarget || !this.listUrlValue) return
    if (this.listTarget.classList.contains("hidden")) return // search results showing
    const due = Date.now() - this.lastResync >= this.resyncIntervalValue
    const oversized = this.itemCount > this.maxItemsValue
    if ((due || oversized) && this.idle) this.resync()
  }

  resync() {
    this.lastResync = Date.now()
    this.savedScrollTop = this.listTarget.scrollTop
    const frame = this.listTarget
    if (frame.getAttribute("src") === this.listUrlValue) {
      frame.reload()
    } else {
      frame.src = this.listUrlValue
    }
  }

  // Turbo swaps the frame's children, so the scroll container itself persists;
  // put the operator back where they were and re-mark the open conversation.
  listResynced(event) {
    if (event.target !== this.listTarget) return
    if (this.currentConversationIdValue) {
      const open = this.listTarget.querySelector(
        `a.conversation-item[data-conversation-id="${this.currentConversationIdValue}"]`
      )
      if (open) open.classList.add(...this.constructor.SELECTED_CLASSES)
    }
    if (this.savedScrollTop != null) {
      this.listTarget.scrollTop = this.savedScrollTop
      this.savedScrollTop = null
    }
  }

  static SELECTED_CLASSES = ["bg-orange-50", "border-l-4", "border-orange-500"]

  // Highlight the clicked item, drop its unread markers (the server marks it
  // read as it renders), and remember it so later broadcasts keep it selected.
  select(event) {
    const item = event.target.closest("a.conversation-item")
    if (!item || event.defaultPrevented) return

    const id = Number(item.dataset.conversationId)
    if (!id) return

    document.querySelectorAll("a.conversation-item").forEach((el) => {
      el.classList.remove(...this.constructor.SELECTED_CLASSES)
    })
    item.classList.add(...this.constructor.SELECTED_CLASSES)

    const dot = item.querySelector("[data-unread-dot]")
    if (dot) { dot.classList.add("hidden"); dot.classList.remove("inline-block") }
    item.querySelector("[data-unread-badge]")?.remove()

    this.currentConversationIdValue = id
  }

  received(data) {
    if (data.type !== "conversation_updated" || !data.html) return

    const isOpen =
      this.currentConversationIdValue && data.conversation_id === this.currentConversationIdValue
    this.upsertItem(data.conversation_id, data.html, data.reposition)

    // Play a sound + flash the title for inbound messages, unless the operator is
    // already looking at that conversation in a focused tab.
    if (data.sound && !(isOpen && !document.hidden)) {
      this.playChime()
      this.flashTitle(data.client_name)
    }
  }

  upsertItem(id, html, reposition = true) {
    const tpl = document.createElement("template")
    tpl.innerHTML = html.trim()
    const node = tpl.content.firstElementChild
    if (!node) return

    const existing = document.getElementById(`conversation-item-${id}`)

    // Preserve the "selected" styling for the currently open conversation (the
    // broadcast renders it unselected).
    if (this.currentConversationIdValue && id === this.currentConversationIdValue) {
      node.classList.add(...this.constructor.SELECTED_CLASSES)
    }

    // Under the unread filter, an update that leaves the conversation read only
    // refreshes a row already on screen — it never adds one. The row an operator
    // just opened stays put; the next resync drops it.
    if (this.unreadOnlyValue && !node.querySelector("[data-unread-badge]")) {
      if (existing) existing.replaceWith(node)
      return
    }

    const empty = document.getElementById("conversation-list-empty")
    if (empty) empty.remove()

    // New activity floats to the top; a state change (closing, assigning) is
    // redrawn where it sits, so the operator's place in the list holds.
    if (existing && reposition === false) {
      existing.replaceWith(node)
    } else {
      if (existing) existing.remove()
      this.listTarget.insertBefore(node, this.listTarget.firstElementChild)
    }

    node.classList.add("ring-2", "ring-orange-300")
    setTimeout(() => node.classList.remove("ring-2", "ring-orange-300"), 1600)
  }

  ensureAudio() {
    if (!this.audioCtx) {
      const AC = window.AudioContext || window.webkitAudioContext
      if (AC) this.audioCtx = new AC()
    }
    if (this.audioCtx && this.audioCtx.state === "suspended") this.audioCtx.resume()
  }

  playChime() {
    this.ensureAudio()
    const ctx = this.audioCtx
    if (!ctx) return

    const now = ctx.currentTime
    // Two short ascending notes.
    ;[[880, 0], [1174.66, 0.12]].forEach(([freq, offset]) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = "sine"
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, now + offset)
      gain.gain.exponentialRampToValueAtTime(0.15, now + offset + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.18)
      osc.connect(gain).connect(ctx.destination)
      osc.start(now + offset)
      osc.stop(now + offset + 0.2)
    })
  }

  flashTitle(name) {
    if (!document.hidden) return
    this.clearTitleFlash()
    const label = name ? `Nuevo mensaje de ${name}` : "Nuevo mensaje"
    let on = false
    this.titleInterval = setInterval(() => {
      document.title = on ? this.originalTitle : `🔔 ${label}`
      on = !on
    }, 1000)
  }

  clearTitleFlash() {
    if (this.titleInterval) {
      clearInterval(this.titleInterval)
      this.titleInterval = null
    }
    document.title = this.originalTitle
  }
}
